import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection
} from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withPreloading,
  PreloadAllModules,
  RouteReuseStrategy
} from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideIonicAngular, IonicRouteStrategy } from '@ionic/angular/standalone';

import { AUTH_HEADER_PROVIDER } from '@vevents/api-client';
import {
  KeyboardService,
  SplashScreenService,
  StatusBarService
} from '@vevents/native-services';

import { routes } from './app.routes';
import { SdkHostBootstrapService } from './core/sdk/sdk-host-bootstrap.service';
import { SdkRuntimeConfigService } from './core/sdk/sdk-runtime-config.service';
import { ThemeService } from './core/services/theme.service';
import { AuthService } from './core/services/auth.service';
import { DeepLinkHandlerService } from './core/services/deep-link-handler.service';
import { SessionDisplacedService } from './core/services/session-displaced.service';
import { DeviceInstallIdService } from './core/services/device-install-id.service';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { sudoInterceptor } from './core/interceptors/sudo.interceptor';
import { MobileHapticsService } from './core/native/mobile-haptics.service';
import {
  NavigationTrackerService,
  TelemetryErrorHandler,
  TelemetryService,
  telemetryInterceptor
} from './core/telemetry';
import { AnalyticsEvent } from '../../analytics-events';

/**
 * MOB-100 Stage 18 — application config for `apps/mobile`.
 *
 * Stage 17 wired the shell (Ionic, theme, status bar, splash). Stage 18
 * adds the auth slice. Stage 28 adds the telemetry slice (Sentry +
 * PostHog/Mixpanel + PII scrubbing + privacy opt-out):
 *  - HTTP interceptors: `sudoInterceptor` MUST run BEFORE
 *    `authInterceptor` so `403 sudo-required` is matched by its own
 *    structured-body check before the bare-401 refresh path could
 *    misinterpret it. `telemetryInterceptor` runs LAST so it observes
 *    final response status + total round-trip duration. Order in
 *    `withInterceptors([...])` is load-bearing (matches the web app's
 *    Stage 15B wiring).
 *  - `AuthService` is published as the `AUTH_HEADER_PROVIDER` for the
 *    shared `@vevents/api-client` so future cross-app HTTP services can
 *    attach a Bearer header without taking a hard dep on the mobile
 *    auth service.
 *  - APP_INITIALIZER chain (in registration order, sequential):
 *      1. Theme bootstrap (Stage 17).
 *      2. Telemetry bootstrap (Stage 28) — must run BEFORE auth so the
 *         cold-start window is captured and so `AuthService.bindUser()`
 *         has a working `TelemetryService` to call.
 *      3. Device-install-id resolution (creates / loads the stable id).
 *      4. AuthService.initialize() — hydrate token bag, refresh active.
 *      5. DeepLinkHandlerService.install() — bind Capacitor App URL.
 *      6. SessionDisplacedService.ensureConnected() — SignalR.
 *      7. Navigation tracker — subscribe to Router events for analytics.
 */
/**
 * F-SDK — must run FIRST. The booth-rep route guard and the Me-tab tool
 * sections read `SdkRuntimeConfigService` synchronously, so the host's
 * configuration has to be resolved before the first route activation. It also
 * decides whether the splash screen is shown at all, which `initializeShell`
 * needs immediately after.
 */
function initializeSdkHostConfig(): () => Promise<void> {
  const sdkConfig = inject(SdkRuntimeConfigService);
  return () => sdkConfig.load();
}

/**
 * F-SDK — turns the host's partner credential into a session, and installs
 * the host-delegated push and notification-tap handoffs. Runs after auth so a
 * still-valid session from a previous presentation is reused.
 */
function initializeSdkHostBridge(): () => Promise<void> {
  const bootstrap = inject(SdkHostBootstrapService);
  return async () => {
    try {
      await bootstrap.install();
    } catch (err) {
      console.warn('[appConfig] SdkHostBootstrapService install failed:', err);
    }
  };
}

function initializeShell(): () => Promise<void> {
  const theme = inject(ThemeService);
  const splash = inject(SplashScreenService);
  const keyboard = inject(KeyboardService);
  const statusBar = inject(StatusBarService);
  const sdkConfig = inject(SdkRuntimeConfigService);

  return async () => {
    // F-SDK REQ-F-SDK-073 — the host owns launch presentation, so hide the
    // splash before resolving the theme rather than after. The standalone app
    // deliberately does the opposite to avoid a flash of unthemed chrome, but
    // inside a host app the splash is a second, unexplained brand appearing
    // over the partner's own launch sequence.
    if (sdkConfig.suppressSplashScreen()) {
      await splash.hide();
    }

    theme.tokens();
    await statusBar.setStyle(theme.statusBarStyle());
    await keyboard.configureForPlatform();
    await splash.hide();
  };
}

function initializeTelemetry(): () => Promise<void> {
  const telemetry = inject(TelemetryService);
  // Resolve to a no-op promise — telemetry must NEVER block first paint
  // per the Stage 28 prompt edge cases (Sentry endpoint unreachable, etc.).
  return async () => {
    try {
      await telemetry.init();
      telemetry.trackEvent(AnalyticsEvent.app_opened, { coldStart: true });
    } catch (err) {
      console.warn('[appConfig] TelemetryService init failed:', err);
    }
  };
}

function initializeDeviceId(): () => Promise<void> {
  const device = inject(DeviceInstallIdService);
  return () => device.describe().then(() => undefined);
}

function initializeAuth(): () => Promise<void> {
  const auth = inject(AuthService);
  return () => auth.initialize();
}

function initializeDeepLinkHandler(): () => Promise<void> {
  const deepLink = inject(DeepLinkHandlerService);
  return () => deepLink.install();
}

function initializeSessionDisplaced(): () => Promise<void> {
  const displaced = inject(SessionDisplacedService);
  // Best-effort AND non-blocking — the SignalR connection must never gate
  // first paint or compete with the cold-launch deep-link resolve for the
  // (native-HTTP) channel. We kick it off without awaiting so APP_INITIALIZER
  // resolves immediately; failures are swallowed.
  return () => {
    void displaced.ensureConnected().catch((err) => {
      console.warn('[appConfig] SessionDisplacedService bootstrap failed:', err);
    });
    return Promise.resolve();
  };
}

function initializeNavigationTracker(): () => void {
  const tracker = inject(NavigationTrackerService);
  return () => tracker.install();
}

/**
 * MOB-100 Stage 22 — hydrate the haptics preference from the
 * server-stored mobile preferences row (REQ-F-MOB-202) so the value
 * is correct on cold boot. Best-effort; failures fall back to the
 * local cached value and do not block first paint.
 */
function initializeHaptics(): () => Promise<void> {
  const haptics = inject(MobileHapticsService);
  // Non-blocking — the server-stored preference hydrate is a best-effort
  // GET that must not gate first paint or contend with the cold-launch
  // deep-link resolve. Kick it off and let it settle in the background.
  return () => {
    void haptics.hydrate().catch((err) => {
      console.warn('[appConfig] MobileHapticsService hydrate failed:', err);
    });
    return Promise.resolve();
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideBrowserGlobalErrorListeners(),
    provideIonicAngular({
      innerHTMLTemplatesEnabled: false
    }),
    provideRouter(routes, withComponentInputBinding(), withPreloading(PreloadAllModules)),
    // Order is load-bearing:
    //   1. sudoInterceptor catches 403 sudo-required before auth.
    //   2. authInterceptor attaches the bearer / refreshes on 401.
    //   3. telemetryInterceptor observes the final response so its timer
    //      includes the refresh-retry path's round-trip too.
    // withFetch() routes through window.fetch, which CapacitorHttp patches
    // on iOS/Android. The default XHR backend can hang against staging CORS.
    provideHttpClient(
      withFetch(),
      withInterceptors([sudoInterceptor, authInterceptor, telemetryInterceptor])
    ),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: AUTH_HEADER_PROVIDER, useExisting: AuthService },
    // Stage 28: global error handler — every uncaught error flows to
    // `TelemetryService.captureException(...)` and into Sentry.
    { provide: ErrorHandler, useClass: TelemetryErrorHandler },
    {
      // F-SDK — first in the chain; everything downstream may read the
      // resolved host configuration.
      provide: APP_INITIALIZER,
      useFactory: initializeSdkHostConfig,
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeShell,
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTelemetry,
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeDeviceId,
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeDeepLinkHandler,
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAuth,
      multi: true
    },
    {
      // F-SDK — after auth, so an existing session short-circuits the
      // credential exchange.
      provide: APP_INITIALIZER,
      useFactory: initializeSdkHostBridge,
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeSessionDisplaced,
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeNavigationTracker,
      multi: true
    },
    {
      // MOB-100 Stage 22 — hydrate the server-stored haptics preference
      // (REQ-F-MOB-202) after auth completes so the bearer header is
      // already attached to the /api/mobile/me/preferences round-trip.
      provide: APP_INITIALIZER,
      useFactory: initializeHaptics,
      multi: true
    }
  ]
};
